import React, { useEffect, useState } from 'react'
import { Radio } from './Radio'

export type RadioGroupProps = {
  error?: boolean
  message?: string
  helpText?: string
  name?: string
  reference?: React.RefObject<HTMLInputElement>
  legend?: string
  options?: string[]
  onOptionSelected?: (selectedOption: string | null) => void
  testId?: string
}

export const RadioGroup = (props: RadioGroupProps) => {
  const {
    error,
    message,
    helpText,
    name,
    reference,
    legend,
    options,
    onOptionSelected,
    testId
  } = props
  const [optionChecked, setOptionChecked] = useState<string | null>(null)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setOptionChecked(event.target.value)
  }

  useEffect(() => {
    onOptionSelected?.(optionChecked)
  }, [optionChecked, onOptionSelected])

  const describedByIds = [
    error && message ? 'errorContainer' : '',
    helpText ? 'helpContainer' : ''
  ]
    .filter(Boolean)
    .join(' ')

  const radios = options?.map((option: string, index: number) => {
    const inputId = `${name ?? 'radio-option'}-${index}`

    return (
      <Radio
        key={`option-${index}`}
        id={inputId}
        name={name ?? 'radio-option'}
        value={option}
        label={option}
        checked={optionChecked === option}
        onChange={handleChange}
        reference={reference}
        testId={testId}
      />
    )
  })

  return (
    <fieldset
      className="Form-group"
      role="radiogroup"
      aria-invalid={error ? 'true' : undefined}
      aria-describedby={describedByIds || undefined}
      data-testid={testId ? `${testId}-fieldset` : undefined}
    >
      {legend && (
        <legend
          className="u-marginBottom8"
          data-testid={testId ? `${testId}-legend` : undefined}
        >
          {legend}
        </legend>
      )}
      {error ? <div className="RadioCheckbox-wrapper">{radios}</div> : radios}
      {error && message && (
        <ul id="errorContainer">
          <li
            className="Form-help Form-help--error"
            data-testid={testId ? `${testId}-message` : undefined}
          >
            <span
              data-testid={testId ? `${testId}-warning` : undefined}
              aria-hidden="true"
            >
              warning
            </span>
            <span className="u-hiddenVisually">Error,</span>
            <span>{message}</span>
          </li>
        </ul>
      )}
      {helpText && (
        <ul id="helpContainer">
          <li
            className="Form-help"
            data-testid={testId ? `${testId}-helpText` : undefined}
          >
            {helpText}
          </li>
        </ul>
      )}
    </fieldset>
  )
}
